import { isNonEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import type { IntlKey } from 'locales';
import type { TextFieldData, TextFieldMetadata } from 'models/forms/fields';
import { FC, FocusEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export const TextField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType, tabIndex }) => {
  const [value, setValue] = useData<TextFieldData>(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const fieldMetadata = useFieldMetadata<TextFieldMetadata>(id);

  const hasTooltip = useMemo(() => fieldMetadata?.hasTooltip ?? false, [fieldMetadata]);
  const tooltipLocation = useMemo(() => fieldMetadata?.tooltipLocation, [fieldMetadata]);

  const isFieldInvalid = useIsFieldInvalid(id);
  const [internalValue, setInternalValue] = useState<Nullable<string>>(value);
  const isReadOnly = useBlockOrFieldReadOnly(id);
  const translationKey = useFieldTranslationKey(id);
  const tooltipKey = hasTooltip ? (`form.${translationKey}.tooltip` as IntlKey) : undefined;
  const { formatMessage } = useIntl();

  const onBlur = useCallback(() => {
    if (value !== internalValue) {
      setValue(internalValue);
    }
  }, [value, internalValue, setValue]);

  const onChange = useCallback((e: FocusEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value || null);
  }, []);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const label = useMemo(
    () =>
      isNonEmpty(internalValue)
        ? formatMessage({
            id: `field.${internalValue}` as IntlKey,
            defaultMessage: `${internalValue}`,
          })
        : '',
    [formatMessage, internalValue],
  );

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as any}
      tooltipKey={tooltipKey}
      workspaceType={workspaceType}
      tooltipLocation={tooltipLocation}
      className={patchedFieldClassName}
    >
      <input
        className={classNames('form-control', { 'pe-none': isReadOnly }, { 'is-warning': isFieldInvalid })}
        data-e2e={id + '-input'}
        id={id}
        name={id}
        onChange={onChange}
        onBlur={onBlur}
        readOnly={isReadOnly}
        type="text"
        value={label}
        tabIndex={tabIndex}
      />
    </BlockOrientationFormGroup>
  );
};

TextField.displayName = 'TextField';
